import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSeedanceTaskList } from '../../api/seedance';
import {
  getEraseVideoSubtitleTaskResult,
  resolveEraseVideoSubtitleTaskOutcome,
  submitEraseVideoSubtitle,
} from '../../api/agiBackTask';
import {
  loadEraseSubtitleTaskMap,
  removeEraseSubtitleTaskMapping,
  saveEraseSubtitleTaskMapping,
  type EraseSubtitleTaskMap,
} from '../../utils/seedanceEraseSubtitleTaskMap';
import { useSeedanceTaskStore } from '../../store/seedanceTaskStore';
import type { Task, TaskStatus } from '../../store/seedanceTypes';
import { useSeedanceUiStore } from '../../store/seedanceUiStore';
import {
  buildTaskDetailPath,
  getTaskDetailIdFromRoute,
  isTaskListRoute,
} from '../../router/seedanceRoutes';
import { extractApiMessage } from '../../utils/seedanceRequestUtils';
import {
  defaultTaskVideoDownloadName,
  ensureVideoDownloadFileName,
  extractPrompt,
  formatTaskListTime,
  getTaskListRecentRangeUnixSeconds,
  isTaskVideoUrlExpired,
  isUsableVideoUrl,
  mergeTaskTimestamps,
  normalizeStatus,
  triggerVideoDownload,
} from '../../utils/seedanceTaskUtils';
import { ConfirmModal } from '../../components/ConfirmModal';
import {
  getLocalStorageValue,
  SEEDANCE_STORAGE_KEYS,
} from '../../utils/seedanceStorage';
import { useSeedanceAuthStore } from '../../store/seedanceAuthStore';
import { TaskCard } from '../../components/TaskCard';
import { loadTaskNameMap, saveTaskName, type TaskNameMap } from '../../utils/seedanceTaskNameMap';
import {
  cancelGlobalSucceededRecompute,
  pickSucceededAggregateFromPayload,
  scheduleGlobalSucceededRecompute,
} from '../../utils/seedanceTaskAggregateSync';
import { TaskDetailPage } from './TaskDetailPage';
import './tasks.css';

interface UseTasksPageControllerArgs {}

/** 任务列表：加载、分页与视频操作。 */
export const useTasksPageController = ({}: UseTasksPageControllerArgs = {}) => {
  const apiKey = useSeedanceAuthStore((s) => s.apiKey);
  const apiKeyRef = useRef(apiKey);
  useEffect(() => {
    apiKeyRef.current = apiKey;
  }, [apiKey]);

  const addDebug = useSeedanceUiStore((s) => s.addDebug);
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const setConfigVisible = useSeedanceUiStore((s) => s.setConfigVisible);
  const openConfig = useCallback(() => setConfigVisible(true), [setConfigVisible]);

  const hasTaskListConfig = useCallback(() => {
    const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
    return Boolean(userId);
  }, []);

  const tasks = useSeedanceTaskStore((s) => s.tasks);
  const setTasks = useSeedanceTaskStore((s) => s.setTasks);
  const taskTotal = useSeedanceTaskStore((s) => s.taskTotal);
  const setTaskTotal = useSeedanceTaskStore((s) => s.setTaskTotal);
  const taskSucceededTotal = useSeedanceTaskStore((s) => s.taskSucceededTotal);
  const setTaskSucceededTotal = useSeedanceTaskStore((s) => s.setTaskSucceededTotal);
  const setModalVideoUrl = useSeedanceUiStore((s) => s.setModalVideoUrl);
  const location = useLocation();
  const tasksEnterHandledRef = useRef(false);

  const [taskPage, setTaskPage] = useState(1);
  const taskPageSize = 12;
  const taskPageRef = useRef(taskPage);
  useEffect(() => {
    taskPageRef.current = taskPage;
  }, [taskPage]);

  const fetchTaskList = useCallback(
    async (targetPage?: number) => {
      if (!hasTaskListConfig()) {
        addDebug('缺少任务接口配置，自动打开配置弹窗', true);
        showToast('请先配置系统访问令牌和用户ID', 'e');
        openConfig();
        return;
      }
      const page = targetPage != null ? Math.max(1, targetPage) : taskPageRef.current;
      addDebug('正在获取任务列表...');
      showToast('正在从API获取任务列表...', 'i');
      try {
        const { startTimestamp, endTimestamp } = getTaskListRecentRangeUnixSeconds();
        const data: any = await getSeedanceTaskList({
          apiKey: apiKeyRef.current,
          onDebug: addDebug,
          page,
          pageSize: taskPageSize,
          taskId: '',
          startTimestamp,
          endTimestamp,
        });
        const taskListSuccess =
          typeof data?.success === 'boolean'
            ? data.success
            : typeof data?.data?.success === 'boolean'
              ? data.data.success
              : undefined;
        if (taskListSuccess === false) {
          const msg = extractApiMessage(data) || '获取任务列表失败';
          throw new Error(msg);
        }
        let items: any[] = Array.isArray(data?.data?.items) ? data.data.items : [];
        const totalFromApi = Number(data?.data?.total);
        const reportedTotal =
          Number.isFinite(totalFromApi) && totalFromApi >= 0 ? totalFromApi : items.length;
        setTaskTotal(reportedTotal);
        const totalPages = Math.max(1, Math.ceil(reportedTotal / taskPageSize));
        const effectivePage = Math.min(page, totalPages);

        const agg = pickSucceededAggregateFromPayload(data?.data);
        if (agg != null) {
          cancelGlobalSucceededRecompute();
          setTaskSucceededTotal(agg);
        } else {
          scheduleGlobalSucceededRecompute({
            totalHint: reportedTotal,
            onDebug: addDebug,
            firstPageItems: items,
            firstPageWasPageOne: effectivePage === 1,
          });
        }

        const apiTasks: Task[] = items.map((item: any) => {
          const innerData = item?.data && typeof item.data === 'object' ? item.data : {};
          const output = item.output || item.result || innerData.output || innerData.result || {};
          const content = item.content || innerData.content || {};
          const remoteId = String(
            item.task_id ||
              item.id ||
              innerData.id ||
              innerData.remoteTaskId ||
              item.remote_task_id ||
              '',
          );
          const detail: Task = {
            id: remoteId,
            remoteTaskId: remoteId,
            prompt: extractPrompt(item) || item.properties?.input || '无描述',
            status: normalizeStatus(item.status || innerData.status),
            video_url: null,
            thumbnail_url: null,
            created_at:
              mergeTaskTimestamps(
                item.created_at,
                item.submit_time,
                innerData.created_at,
                item.create_time,
              ) ?? new Date().toISOString(),
            completed_at: mergeTaskTimestamps(
              item.finish_time,
              item.completed_at,
              innerData.completed_at,
              innerData.finish_time,
              innerData.updated_at,
              item.updated_at,
            ),
            updated_at: mergeTaskTimestamps(
              item.updated_at,
              innerData.updated_at,
              item.modified_at,
              innerData.modified_at,
            ),
            finished_at: mergeTaskTimestamps(
              item.finished_at,
              innerData.finished_at,
              item.finish_time,
              innerData.finish_time,
            ),
            model: item.model || innerData.model || item.properties?.upstream_model_name,
            duration: item.duration || innerData.duration,
            ratio: innerData.ratio || item.ratio,
            resolution: innerData.resolution || item.resolution,
            error:
              innerData.error?.message ||
              item.fail_reason ||
              item.error?.message ||
              item.error ||
              null,
          };

          if (isUsableVideoUrl(output.video_url)) detail.video_url = output.video_url;
          else if (isUsableVideoUrl(output.video)) detail.video_url = output.video;
          else if (isUsableVideoUrl(output.url)) detail.video_url = output.url;
          else if (isUsableVideoUrl(content.video_url)) detail.video_url = content.video_url;
          else if (isUsableVideoUrl(item.result_url)) detail.video_url = item.result_url;
          else if (isUsableVideoUrl(item.video_url)) detail.video_url = item.video_url;

          if (output.thumbnail_url) detail.thumbnail_url = output.thumbnail_url;
          else if (output.thumbnail) detail.thumbnail_url = output.thumbnail;
          else if (content && content.thumbnail_url) detail.thumbnail_url = content.thumbnail_url;
          else if (item.thumbnail_url) detail.thumbnail_url = item.thumbnail_url;
          if (item.context) detail.context = item.context;
          return detail;
        });

        apiTasks.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );

        if (effectivePage === 1) {
          setTasks((prev) => {
            const locals = prev.filter((t) => String(t.id).startsWith('pending_'));
            const seen = new Set(apiTasks.map((t) => t.id));
            const keepLocals = locals.filter((t) => !seen.has(t.id));
            const next = [...keepLocals, ...apiTasks];
            next.sort(
              (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
            );
            return next;
          });
        } else {
          setTasks(apiTasks);
        }

        setTaskPage(effectivePage);
        taskPageRef.current = effectivePage;

        addDebug(
          `任务列表：第 ${effectivePage}/${totalPages} 页，接口 ${items.length} 条，total=${reportedTotal}（24h 内 start=${startTimestamp} end=${endTimestamp}）`,
        );
        showToast(`第 ${effectivePage} 页：已加载 ${items.length} 条任务`, 's');
      } catch (e: any) {
        const msg =
          extractApiMessage(e) ||
          extractApiMessage(e?.data) ||
          String(e?.message || e) ||
          '获取任务列表失败';
        addDebug(`获取任务列表失败: ${msg}`, true);
        showToast(msg, 'e');
      }
    },
    [
      addDebug,
      hasTaskListConfig,
      openConfig,
      setTaskSucceededTotal,
      setTaskTotal,
      setTasks,
      showToast,
      taskPageSize,
    ],
  );

  useEffect(() => {
    if (!isTaskListRoute(location.pathname)) {
      tasksEnterHandledRef.current = false;
      return;
    }
    if (tasksEnterHandledRef.current) return;
    tasksEnterHandledRef.current = true;
    setTaskPage(1);
    taskPageRef.current = 1;
    setTasks((prev) => prev.filter((t) => String(t.id).startsWith('pending_')));
    if (hasTaskListConfig()) {
      void fetchTaskList(1);
    }
  }, [location.pathname, hasTaskListConfig, fetchTaskList, setTasks]);

  const refreshTasks = useCallback(() => {
    void fetchTaskList(taskPageRef.current);
  }, [fetchTaskList]);

  const playVideo = useCallback(
    (url: string) => setModalVideoUrl(url),
    [setModalVideoUrl],
  );

  const completedCount = taskSucceededTotal;
  const totalTaskPages = useMemo(
    () => Math.max(1, Math.ceil((taskTotal || 0) / taskPageSize)),
    [taskTotal, taskPageSize],
  );
  const pagedTasks = useMemo(() => tasks, [tasks]);

  useEffect(() => {
    setTaskPage((prev) => Math.min(prev, totalTaskPages));
  }, [totalTaskPages]);

  return {
    tasks,
    pagedTasks,
    taskPage,
    totalTaskPages,
    taskTotal,
    completedCount,
    fetchTaskList,
    refreshTasks,
    openConfig,
    playVideo,
    hasTaskListConfig,
    setTaskPage,
  };
};

const statusLabel: Record<TaskStatus, string> = {
  pending: '等待中',
  processing: '生成中',
  succeeded: '已完成',
  failed: '失败',
};

const TasksListPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    tasks,
    taskTotal,
    pagedTasks,
    taskPage,
    totalTaskPages,
    fetchTaskList,
    refreshTasks,
    openConfig,
    playVideo,
    hasTaskListConfig,
    setTaskPage,
  } = useTasksPageController();

  const [downloadTarget, setDownloadTarget] = useState<{ url: string } | null>(null);
  const [downloadFileName, setDownloadFileName] = useState('');
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [eraseSubtitleBusyTaskId, setEraseSubtitleBusyTaskId] = useState<string | null>(null);
  const [eraseSubtitleResultBusyTaskId, setEraseSubtitleResultBusyTaskId] = useState<string | null>(
    null,
  );
  const [eraseSubtitleTaskMap, setEraseSubtitleTaskMap] = useState<EraseSubtitleTaskMap>({});
  const [taskNameMap, setTaskNameMap] = useState<TaskNameMap>(() => loadTaskNameMap());
  const downloadInputRef = useRef<HTMLInputElement>(null);
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const addDebug = useSeedanceUiStore((s) => s.addDebug);
  const apiKey = useSeedanceAuthStore((s) => s.apiKey);
  const configUserId = useSeedanceAuthStore((s) => s.configUserId);

  const refreshEraseSubtitleTaskMap = useCallback(async () => {
    try {
      const map = await loadEraseSubtitleTaskMap();
      setEraseSubtitleTaskMap(map);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '加载擦除字幕记录失败';
      addDebug(msg, true);
    }
  }, [addDebug]);

  useEffect(() => {
    void refreshEraseSubtitleTaskMap();
  }, [refreshEraseSubtitleTaskMap]);

  useEffect(() => {
    if (!downloadTarget) return;
    const timer = window.setTimeout(() => {
      downloadInputRef.current?.focus();
      downloadInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [downloadTarget]);

  const closeDownloadDialog = useCallback(() => {
    if (downloadBusy) return;
    setDownloadTarget(null);
    setDownloadFileName('');
  }, [downloadBusy]);

  const renameTask = useCallback((taskId: string, name: string) => {
    const id = (taskId || '').trim();
    if (!id) return;
    setTaskNameMap(saveTaskName(id, name));
  }, []);

  const resolveTaskDownloadName = useCallback(
    (task: Task) => {
      const listTaskId = (task.remoteTaskId || task.id || '').trim();
      const customTaskName = listTaskId ? taskNameMap[listTaskId] : '';
      return defaultTaskVideoDownloadName(task, { customTaskName });
    },
    [taskNameMap],
  );

  /** 浏览器弹命名框；Electron 走系统另存为。任务下载与擦除结果下载共用。 */
  const startVideoDownload = useCallback(
    async (url: string, defaultName: string) => {
      const videoUrl = (url || '').trim();
      if (!videoUrl) {
        showToast('视频地址无效', 'e');
        return;
      }
      const fileName = ensureVideoDownloadFileName(defaultName);

      if (typeof window.electronAPI?.downloadFile === 'function') {
        setDownloadBusy(true);
        try {
          const result = await triggerVideoDownload(videoUrl, fileName);
          showToast(result.filePath ? `已保存到：${result.filePath}` : '下载完成', 's');
        } catch (err) {
          const msg = err instanceof Error ? err.message : '下载失败';
          if (msg !== '已取消下载') showToast(msg, 'e');
        } finally {
          setDownloadBusy(false);
        }
        return;
      }

      setDownloadFileName(defaultName);
      setDownloadTarget({ url: videoUrl });
    },
    [showToast],
  );

  const openDownloadDialog = useCallback(
    async (url: string, task: Task) => {
      await startVideoDownload(url, resolveTaskDownloadName(task));
    },
    [resolveTaskDownloadName, startVideoDownload],
  );

  const eraseSubtitle = useCallback(
    async (url: string, sourceTaskId: string) => {
      const videoUrl = (url || '').trim();
      const listTaskId = (sourceTaskId || '').trim();
      if (!videoUrl) {
        showToast('视频地址无效', 'e');
        return;
      }
      if (!listTaskId) {
        showToast('任务 ID 无效', 'e');
        return;
      }
      if (!hasTaskListConfig()) {
        showToast('请先配置系统访问令牌和用户 ID', 'e');
        openConfig();
        return;
      }
      if (eraseSubtitleBusyTaskId) return;
      setEraseSubtitleBusyTaskId(listTaskId);
      showToast('正在提交字幕擦除任务…', 'i');
      try {
        const result = await submitEraseVideoSubtitle(videoUrl, {
          systemToken: apiKey,
          userId: configUserId,
          onDebug: (msg, isError) => addDebug(msg, isError),
        });
        const eraseTaskId = (result.task_id || '').trim();
        if (!eraseTaskId) {
          throw new Error('未获取到任务 ID');
        }
        await saveEraseSubtitleTaskMapping(listTaskId, eraseTaskId, { videoUrl });
        setEraseSubtitleTaskMap((prev) => ({ ...prev, [listTaskId]: eraseTaskId }));
        addDebug(`字幕擦除映射已保存: ${listTaskId} -> ${eraseTaskId}`);
        showToast('字幕擦除任务已提交', 's');
      } catch (err) {
        const msg = err instanceof Error ? err.message : '字幕擦除任务提交失败';
        addDebug(`字幕擦除提交失败: ${msg}`, true);
        showToast(msg, 'e');
      } finally {
        setEraseSubtitleBusyTaskId(null);
      }
    },
    [
      addDebug,
      configUserId,
      eraseSubtitleBusyTaskId,
      hasTaskListConfig,
      openConfig,
      showToast,
      apiKey,
    ],
  );

  const fetchEraseSubtitleResult = useCallback(
    async (sourceTaskId: string, task: Task) => {
      const listTaskId = (sourceTaskId || '').trim();
      if (!listTaskId) {
        showToast('任务 ID 无效', 'e');
        return;
      }
      const eraseTaskId = (eraseSubtitleTaskMap[listTaskId] || '').trim();
      if (!eraseTaskId) {
        showToast('未找到擦除字幕记录', 'e');
        void refreshEraseSubtitleTaskMap();
        return;
      }
      if (!hasTaskListConfig()) {
        showToast('请先配置系统访问令牌和用户 ID', 'e');
        openConfig();
        return;
      }
      if (eraseSubtitleResultBusyTaskId) return;

      setEraseSubtitleResultBusyTaskId(listTaskId);
      showToast('正在查询擦除字幕结果…', 'i');
      try {
        const payload = await getEraseVideoSubtitleTaskResult(eraseTaskId, {
          systemToken: apiKey,
          userId: configUserId,
          onDebug: (msg, isError) => addDebug(msg, isError),
        });
        const outcome = resolveEraseVideoSubtitleTaskOutcome(payload);

        if (outcome.kind === 'ready') {
          await startVideoDownload(
            outcome.videoUrl,
            `${resolveTaskDownloadName(task)}_nosub`,
          );
          return;
        }

        if (outcome.kind === 'pending') {
          showToast('擦除字幕任务尚未完成，请稍后再试', 'i');
          return;
        }

        showToast(outcome.message || '擦除字幕任务失败', 'e');
        await removeEraseSubtitleTaskMapping(listTaskId);
        setEraseSubtitleTaskMap((prev) => {
          const next = { ...prev };
          delete next[listTaskId];
          return next;
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : '查询擦除字幕结果失败';
        addDebug(`擦除字幕结果查询失败: ${msg}`, true);
        showToast(msg, 'e');
      } finally {
        setEraseSubtitleResultBusyTaskId(null);
      }
    },
    [
      addDebug,
      configUserId,
      eraseSubtitleResultBusyTaskId,
      eraseSubtitleTaskMap,
      hasTaskListConfig,
      openConfig,
      refreshEraseSubtitleTaskMap,
      showToast,
      resolveTaskDownloadName,
      startVideoDownload,
      apiKey,
    ],
  );

  const confirmDownload = useCallback(async () => {
    if (!downloadTarget || downloadBusy) return;
    const url = downloadTarget.url;
    const fileName = ensureVideoDownloadFileName(downloadFileName);
    setDownloadBusy(true);
    showToast('正在准备下载…', 'i');
    try {
      const result = await triggerVideoDownload(url, fileName);
      showToast(
        result.filePath ? `已保存到：${result.filePath}` : `已开始下载：${fileName}`,
        's',
      );
      setDownloadTarget(null);
      setDownloadFileName('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '下载失败';
      showToast(msg, 'e');
    } finally {
      setDownloadBusy(false);
    }
  }, [downloadBusy, downloadFileName, downloadTarget, showToast]);

  const goDetail = useCallback(
    (task: Task) => {
      if (!hasTaskListConfig()) {
        openConfig();
        return;
      }
      const id = (task.remoteTaskId || task.id || '').trim();
      if (!id) return;
      navigate(buildTaskDetailPath(id));
    },
    [hasTaskListConfig, navigate, openConfig],
  );

  return (
    <>
      <ConfirmModal
        open={downloadTarget != null}
        title="下载视频"
        confirmLabel="下载"
        confirmBusyLabel="下载中…"
        busy={downloadBusy}
        onCancel={closeDownloadDialog}
        onConfirm={confirmDownload}
      >
        <p style={{ marginBottom: 10 }}>为视频文件命名，确认后开始下载。</p>
        <label className="task-download-name-label" htmlFor="task-download-name">
          文件名
        </label>
        <input
          id="task-download-name"
          ref={downloadInputRef}
          className="task-download-name-input"
          type="text"
          value={downloadFileName}
          onChange={(e) => setDownloadFileName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !downloadBusy) {
              e.preventDefault();
              void confirmDownload();
            }
          }}
          disabled={downloadBusy}
          placeholder="seedance-video"
          autoComplete="off"
        />
        <p className="task-download-name-hint">未填写扩展名时将自动添加 .mp4</p>
      </ConfirmModal>
      <div className="tasks-panel on">
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn refresh" onClick={refreshTasks}>
            🔄 刷新列表
          </button>
          <button type="button" className="btn refresh" onClick={openConfig} title="配置 Token">
            ⚙️ 配置
          </button>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            仅展示最近 24 小时内创建的任务；视频链接同期失效，请及时下载备份
          </span>
        </div>
      </div>
      <div className="tasks-list">
        {taskTotal === 0 && tasks.length === 0 ? (
          <div className="tasks-list-empty">
            暂无任务
            <br />
            点击「刷新列表」拉取远端任务
          </div>
        ) : pagedTasks.length === 0 ? (
          <div className="tasks-list-empty">暂无最近 24 小时内的任务</div>
        ) : (
          pagedTasks.map((t) => {
            const isProcessing = t.status === 'pending' || t.status === 'processing';
            const rawVideo =
              (t.video_url && isUsableVideoUrl(t.video_url) ? t.video_url : '') ||
              (t.thumbnail_url && isUsableVideoUrl(t.thumbnail_url) ? t.thumbnail_url : '');
            const videoExpired = isTaskVideoUrlExpired(t);
            const videoSrc = videoExpired ? '' : rawVideo;
            const mediaHint =
              t.status === 'succeeded' && !isProcessing && videoExpired
                ? '视频链接已超过 24 小时有效期，仅保留任务信息。'
                : null;
            const errorMsg = t?.error || '';
            const timeRaw =
              t.status === 'succeeded' || t.status === 'failed'
                ? t.finished_at || t.completed_at || t.updated_at || t.created_at
                : t.updated_at || t.created_at || t.finished_at || t.completed_at;
            const timeStr = formatTaskListTime(timeRaw);

            const listTaskId = (t.remoteTaskId || t.id || '').trim();

            return (
              <TaskCard
                key={t.id}
                status={t.status}
                statusLabel={statusLabel[t.status] || t.status}
                timeStr={timeStr}
                model={t.model}
                taskName={listTaskId ? taskNameMap[listTaskId] : undefined}
                onRenameTask={
                  listTaskId ? (name) => renameTask(listTaskId, name) : undefined
                }
                duration={t.duration}
                error={errorMsg}
                videoSrc={videoSrc}
                isProcessing={isProcessing}
                mediaHint={mediaHint}
                onPlay={playVideo}
                onDownload={(url) => openDownloadDialog(url, t)}
                sourceTaskId={listTaskId}
                hasEraseSubtitleRecord={Boolean(listTaskId && eraseSubtitleTaskMap[listTaskId])}
                onEraseSubtitle={eraseSubtitle}
                onEraseSubtitleResult={(id) => void fetchEraseSubtitleResult(id, t)}
                eraseSubtitleBusyTaskId={eraseSubtitleBusyTaskId}
                eraseSubtitleResultBusyTaskId={eraseSubtitleResultBusyTaskId}
                onDetail={() => goDetail(t)}
              />
            );
          })
        )}
      </div>
      {taskTotal > 0 && totalTaskPages > 1 ? (
        <div className="tasks-pagination">
          <button
            className="btn"
            type="button"
            onClick={() => void fetchTaskList(taskPage - 1)}
            disabled={taskPage <= 1}
          >
            上一页
          </button>
          <span className="tasks-pagination-text">
            第 {taskPage} / {totalTaskPages} 页
          </span>
          <button
            className="btn"
            type="button"
            onClick={() => void fetchTaskList(taskPage + 1)}
            disabled={taskPage >= totalTaskPages}
          >
            下一页
          </button>
        </div>
      ) : null}
    </div>
    </>
  );
};

const TaskDetailRoute: React.FC = () => {
  const { taskId: rawId } = useParams<{ taskId: string }>();
  const location = useLocation();
  const { openConfig, hasTaskListConfig } = useTasksPageController();

  const taskId = useMemo(() => {
    const fromParam = rawId ? decodeURIComponent(rawId).trim() : '';
    if (fromParam) return fromParam;
    return getTaskDetailIdFromRoute(location.pathname) || '';
  }, [rawId, location.pathname]);

  if (!taskId) {
    return (
      <div className="tasks-panel on">
        <p className="task-detail-error">无效的任务 ID</p>
      </div>
    );
  }

  return (
    <TaskDetailPage
      taskId={taskId}
      hasTaskListConfig={hasTaskListConfig}
      onNeedConfig={openConfig}
    />
  );
};

const TasksPage: React.FC = () => {
  return (
    <Routes>
      <Route index element={<TasksListPage />} />
      <Route path=":taskId" element={<TaskDetailRoute />} />
    </Routes>
  );
};

export default TasksPage;
